/**
 * Business Central OAuth2 Authentication Service
 * Delegated flow - user clicks Connect, signs in with Microsoft, tokens stored per org.
 * Same pattern as Fortnox.
 */

import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { app } from '../firebase';

const functions = getFunctions(app, 'europe-west1');

if (process.env.NODE_ENV === 'development') {
  try {
    connectFunctionsEmulator(functions, 'localhost', 5001);
  } catch {
    // Already connected
  }
}

const BC_SCOPE = 'offline_access https://api.businesscentral.dynamics.com/Financials.ReadWrite.All';

class BCAuth {
  constructor() {
    this.clientId = process.env.REACT_APP_BC_CLIENT_ID || '';
    this.redirectUri = process.env.REACT_APP_BC_REDIRECT_URI
      || (typeof window !== 'undefined' ? `${window.location.origin}/bc/callback` : 'https://app.svenry.com/bc/callback');

    this.exchangeCodeFunction = httpsCallable(functions, 'exchangeBCCode');
    this.saveTokensFunction = httpsCallable(functions, 'saveBCTokens');
    this.getStatusFunction = httpsCallable(functions, 'getBusinessCentralStatus');
    this.refreshTokenFunction = httpsCallable(functions, 'refreshBCToken');
  }

  generateSecureState() {
    if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
      const array = new Uint32Array(4);
      window.crypto.getRandomValues(array);
      return Array.from(array, (dec) => dec.toString(36)).join('');
    }
    return Math.random().toString(36).slice(2, 15) + Math.random().toString(36).slice(2, 15) + Date.now().toString(36);
  }

  getAuthorizationUrl(state = null) {
    if (!this.clientId) {
      throw new Error('BC Client ID not configured (REACT_APP_BC_CLIENT_ID)');
    }
    const stateValue = state || this.generateSecureState();
    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.setItem('bc_oauth_state', stateValue);
    }

    const params = new URLSearchParams({
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      response_type: 'code',
      scope: BC_SCOPE,
      state: stateValue,
      response_mode: 'query',
      prompt: 'consent',
    });

    return `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?${params.toString()}`;
  }

  async exchangeCodeForToken(code, state) {
    const result = await this.exchangeCodeFunction({
      code,
      redirectUri: this.redirectUri,
      state,
    });
    return result.data;
  }

  async saveTokens(tokens) {
    const result = await this.saveTokensFunction({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      expiresIn: tokens.expiresIn,
      expiresAt: tokens.expiresAt,
      tenantId: tokens.tenantId,
    });
    return result.data;
  }

  async getConnectionStatus() {
    const result = await this.getStatusFunction();
    return result.data;
  }

  async refreshAccessToken() {
    const result = await this.refreshTokenFunction();
    return result.data;
  }

  connect() {
    if (!this.clientId) {
      throw new Error('BC Client ID not configured. Add REACT_APP_BC_CLIENT_ID to your .env file and rebuild.');
    }
    const state = this.generateSecureState();
    window.location.href = this.getAuthorizationUrl(state);
  }
}

const bcAuth = new BCAuth();
export default bcAuth;
